import { twJoin } from "tailwind-merge";

import { PeriodPicker, type PeriodPickerProps } from "@/components/PeriodPicker/PeriodPicker";
import { cardSurfaceVariants } from "@/design-system/Card";
import { Info } from "@/design-system/Info";
import { Inline } from "@/design-system/Inline";
import { Select, type SelectProps } from "@/design-system/Select";
import { twResolve } from "@/helpers/tw-resolve";

interface AnalyticsCardWrapperProps<T extends React.ElementType> extends Omit<
  React.ComponentPropsWithRef<"article">,
  "className"
> {
  variant?: "small" | "graph" | "content" | "sentimentGraph";
  height?: "content";
  contentAlignment?: "start";
  horizontalScroll?: boolean;
  as?: T;
  ref?: React.Ref<HTMLDivElement>;
}

function AnalyticsCardWrapper<T extends React.ElementType>({
  as,
  ref,
  variant,
  height,
  contentAlignment,
  horizontalScroll,
  ...props
}: AnalyticsCardWrapperProps<T>): React.ReactNode {
  const Component = as || "article";

  return (
    <Component
      {...props}
      ref={ref}
      className={twJoin(
        twResolve(
          "flex size-full flex-col gap-space-md p-space-lg",
          variant === "graph" && "min-h-scroll-area-compact @md:min-h-scroll-area-standard",
          variant === "content" && "h-fit",
          variant === "sentimentGraph" && "h-fit min-h-scroll-area-standard",
          height === "content" && "h-fit",
          contentAlignment === "start" && "justify-start",
          horizontalScroll && "overflow-x-auto",
        ),
        cardSurfaceVariants(),
      )}
    />
  );
}

function AnalyticsCardHeader({
  title,
  tooltip,
  children,
}: React.PropsWithChildren<{ title: string; tooltip?: string }>): React.ReactNode {
  return (
    <Inline gap="md" justify="between" collapseBelow="md">
      <Inline gap="sm" align="center" wrap={false}>
        {tooltip ? <Info tooltip={tooltip} /> : null}
        <h3 className="text-heading-3">{title}</h3>
      </Inline>
      <div className="flex w-full flex-wrap items-center gap-space-sm @md:w-auto @md:justify-end">{children}</div>
    </Inline>
  );
}

/** Renders an analytics select with responsive sizing and a bottom-right portal popup. */
function AnalyticsFilterSelect<
  T,
  TSelected extends T | undefined = T | undefined,
  TEmptyItem extends React.ReactNode | undefined = undefined,
>(props: SelectProps<T, TSelected, TEmptyItem>): React.ReactNode {
  return <Select {...props} width="responsiveFilter" placement="bottom-right" usePortal />;
}

/** Renders an analytics period picker with responsive sizing and bottom-right placement. */
function AnalyticsFilterPeriod(props: PeriodPickerProps): React.ReactNode {
  return (
    <div className="w-full @sm:w-select-responsive-filter">
      <PeriodPicker {...props} fullWidth placement="bottomRight" />
    </div>
  );
}

export const AnalyticsCard = {
  Root: AnalyticsCardWrapper,
  Header: AnalyticsCardHeader,
  FilterSelect: AnalyticsFilterSelect,
  FilterPeriod: AnalyticsFilterPeriod,
};
